import { useEditor, EditorContent } from '@tiptap/react'
import { dedupeReferenceItems, referenceValue, referenceLabel } from "./references";
import { Extension, Mark, Node as TipTapNode, mergeAttributes } from '@tiptap/core'
import type { Editor } from '@tiptap/core'
import { Plugin, PluginKey, Selection } from '@tiptap/pm/state'
import type { Transaction } from 'prosemirror-state'
import { Decoration, DecorationSet } from '@tiptap/pm/view'
import { StarterKit } from '@tiptap/starter-kit'
import { Underline } from '@tiptap/extension-underline'
import { Link } from '@tiptap/extension-link'
import { Table } from '@tiptap/extension-table'
import { TableRow } from '@tiptap/extension-table-row'
import { TableHeader } from '@tiptap/extension-table-header'
import { TableCell } from '@tiptap/extension-table-cell'

// Extends TableHeader to persist conditional formatting attributes through TipTap's schema
const CustomTableHeader = TableHeader.extend({
  addAttributes() {
    return {
      ...this.parent?.(),
      dataField: {
        default: null,
        parseHTML: (el: HTMLElement) => el.getAttribute('data-field'),
        renderHTML: (attrs: Record<string, any>) =>
          attrs.dataField ? { 'data-field': attrs.dataField } : {},
      },
      dataConditionalRules: {
        default: null,
        parseHTML: (el: HTMLElement) => el.getAttribute('data-conditional-rules'),
        renderHTML: (attrs: Record<string, any>) =>
          attrs.dataConditionalRules ? { 'data-conditional-rules': attrs.dataConditionalRules } : {},
      },
    }
  }
})
import { marked } from "marked";
import React, { useState, useEffect, useCallback, useRef, useMemo } from "react";
import TurndownService from "turndown";
import { Icon, cn, Button, Checkbox, Modal, Spinner, evaluateConditionalRules } from '@aidkitorg/component-library'
import { TrackedRecord, UserActivity, createFromRawValue, openChangeset, closeChangeset } from "./collab";
import { NodeProps } from "./editor";
import { useDebounce } from "./utils";
import { Dropdown } from "./Components/Dropdown";
import { stripPastedFontStyles } from "./richtextPasteSanitizer";

// Inject rich-text-content styles once at module load time so any consumer
// automatically gets them without needing to import a CSS file separately.
const RICH_TEXT_STYLES = `
.rich-text-content h1 { font-size: 2em; font-weight: 700; margin-top: 0.5em; margin-bottom: 0.25em; line-height: 1.2; }
.rich-text-content h2 { font-size: 1.5em; font-weight: 700; margin-top: 0.5em; margin-bottom: 0.25em; line-height: 1.3; }
.rich-text-content h3 { font-size: 1.25em; font-weight: 600; margin-top: 0.5em; margin-bottom: 0.25em; line-height: 1.4; }
.rich-text-content h4 { font-size: 1em; font-weight: 600; margin-top: 0.5em; margin-bottom: 0.25em; line-height: 1.5; }
.rich-text-content h5 { font-size: 0.875em; font-weight: 600; margin-top: 0.5em; margin-bottom: 0.25em; line-height: 1.5; }
.rich-text-content ul { list-style-type: disc; padding-left: 1.5em; margin-top: 0.25em; margin-bottom: 0.25em; }
.rich-text-content ol { list-style-type: decimal; padding-left: 1.5em; margin-top: 0.25em; margin-bottom: 0.25em; }
.rich-text-content li { margin-top: 0.125em; margin-bottom: 0.125em; }
.rich-text-content p { margin-top: 0.25em; margin-bottom: 0.25em; }
.rich-text-content p:first-child, .rich-text-content h1:first-child, .rich-text-content h2:first-child, .rich-text-content h3:first-child, .rich-text-content h4:first-child, .rich-text-content h5:first-child { margin-top: 0; }
.rich-text-content hr { border: none; border-top: 1px solid #d1d5db; margin-top: 0.75em; margin-bottom: 0.75em; }
.rich-text-content u { text-decoration: underline; }
.rich-text-content a { color: var(--ak-color-typography-link); text-decoration: underline; cursor: pointer; }
.rich-text-content a:hover { color: var(--ak-color-typography-link-hover); }
.link-selection-highlight { background-color: rgba(59, 130, 246, 0.25); border-radius: 2px; }
.rich-text-content table { border-collapse: collapse; margin-top: 0.5em; margin-bottom: 0.5em; }
.rich-text-content th, .rich-text-content td { border-width: 1px !important; border-style: solid !important; border-color: #9ca3af !important; padding: 0.375em 0.5em; vertical-align: top; position: relative; min-width: 1em; box-sizing: border-box; }
.rich-text-content th { background-color: #f3f4f6; font-weight: 600; text-align: left; }
.rich-text-content .selectedCell::after { background: rgba(99, 102, 241, 0.15); content: ""; left: 0; right: 0; top: 0; bottom: 0; pointer-events: none; position: absolute; }
.rich-text-content .column-resize-handle { background-color: #6366f1; bottom: -2px; pointer-events: none; position: absolute; right: -2px; top: 0; width: 4px; }
.rich-text-content.resize-cursor { cursor: col-resize; }
`;
if (typeof document !== 'undefined') {
  const styleId = 'aidkit-rich-text-styles';
  const existing = document.getElementById(styleId);
  if (existing) {
    existing.textContent = RICH_TEXT_STYLES;
  } else {
    const style = document.createElement('style');
    style.id = styleId;
    style.textContent = RICH_TEXT_STYLES;
    document.head.appendChild(style);
  }
}

// Extension that renders a persistent highlight decoration for the link setting
const linkHighlightKey = new PluginKey('linkHighlight')

function createLinkHighlightExtension(rangeRef: React.MutableRefObject<{ from: number; to: number } | null>) {
  return Extension.create({
    name: 'linkHighlight',
    addProseMirrorPlugins() {
      return [
        new Plugin({
          key: linkHighlightKey,
          props: {
            decorations(state) {
              const range = rangeRef.current
              if (!range || range.from >= range.to) return DecorationSet.empty
              return DecorationSet.create(state.doc, [
                Decoration.inline(range.from, range.to, { class: 'link-selection-highlight' })
              ])
            }
          }
        })
      ]
    }
  })
}

/**
 * Returns whether an element carries custom attributes that require it to appear as raw HTML
 * within the markdown output so they survive the HTML → markdown → HTML round-trip. Covers:
 *   style / class  — inline styles and CSS classes on any element
 *   data-*         — custom data attributes (e.g. data-conditional-rules on <th>)
 *   mark           — always preserved as raw HTML
 */
function nodeHasStyledHtml(node: Node): node is HTMLElement {
  if (node.nodeType !== 1) return false;
  const el = node as HTMLElement;
  if (el.tagName === 'MARK') return true;
  const hasDataAttr = Array.from(el.attributes).some(a => a.name.startsWith('data-'));
  return !!(el.getAttribute('style') || el.getAttribute('class') || hasDataAttr);
}

// Initialize markdown converter

// Keep blank elements that have styled HTML
function blankReplacementPreservingStyledHtml(_content: string, node: Node): string {
  if (!nodeHasStyledHtml(node)) return '';
  const isBlock = 'isBlock' in node && node.isBlock;
  return isBlock ? `\n\n${node.outerHTML}\n\n` : node.outerHTML;
}

const turndownService = new TurndownService({
  headingStyle: 'atx',
  codeBlockStyle: 'fenced',
  blankReplacement: blankReplacementPreservingStyledHtml,
});

// Override list item rule to produce tight lists (no blank lines between items).
turndownService.addRule('listItem', {
  filter: 'li',
  replacement(content, node) {
    const trimmed = content
      .replace(/^\n+/, '')
      .replace(/\n+$/, '\n')
      .replace(/\n/gm, '\n    ')
    const parent = (node as HTMLElement).parentNode as HTMLElement
    const prefix = parent?.nodeName === 'OL'
      ? `${Array.prototype.indexOf.call(parent.children, node) + 1}. `
      : '- '
    return prefix + trimmed + (node.nextSibling ? '\n' : '')
  }
})

// Preserve <u> as inline HTML in markdown (no standard markdown syntax for underline)
turndownService.addRule('underline', {
  filter: 'u',
  replacement: (content) => `<u>${content}</u>`
});

// Matches a <div> whose only child is a <br> — the HtmlBreakNode pattern.
// Used in both the Turndown filter and HtmlBreakNode.parseHTML getAttrs.
function isSingleBrDiv(el: Element): boolean {
  return el.childNodes.length === 1 && el.childNodes[0].nodeName === 'BR';
}

// Preserve <div><br/></div> (HtmlBreakNode) as raw HTML in markdown output so that
// marked passes it through unchanged on the return trip (textarea → RTE). Outputting
// \n\n instead would cause marked to emit nothing, losing the break on round-trip.
// Added before preserveStyledHtml so it takes priority over that fallback rule.
turndownService.addRule('htmlBreak', {
  filter: (node) => node.nodeName === 'DIV' && isSingleBrDiv(node as Element),
  replacement: () => '\n\n<div><br/></div>\n\n',
});

// Keep any element that carries custom attributes as raw HTML within the markdown
// output so they survive the HTML → markdown → HTML round-trip.
// Added before the more specific table/link rules so those take priority over this fallback.
turndownService.addRule('preserveStyledHtml', {
  filter: nodeHasStyledHtml,
  replacement: (_content: string, node: Node) => (node as HTMLElement).outerHTML,
});

// Preserve tables as raw HTML — Turndown has no native table support and would strip them.
// marked passes inline HTML through unchanged, so this round-trips correctly.
// Added after preserveStyledHtml so it takes priority for styled tables (e.g. tiptap-table class).
turndownService.addRule('table', {
  filter: ['table'],
  replacement: (_content, node) => '\n\n' + (node as HTMLElement).outerHTML + '\n\n'
});

// Preserve <iframe> as a raw HTML block so all attributes (src, allow, allowfullscreen, etc.)
// survive the HTML → markdown → HTML round-trip unchanged.
turndownService.addRule('iframe', {
  filter: 'iframe',
  replacement: (_content, node) => '\n\n' + (node as HTMLElement).outerHTML + '\n\n',
});

// Preserve <button> as a raw HTML block so onclick and other attributes survive the
// HTML → markdown → HTML round-trip unchanged. Without this, a bare <button onclick=...>
// (no style/class/data-* attribute) falls through preserveStyledHtml and gets unwrapped
// to plain text, losing the tag and onclick entirely.
turndownService.addRule('button', {
  filter: 'button',
  replacement: (_content, node) => '\n\n' + (node as HTMLElement).outerHTML + '\n\n',
});

// Preserve <a><img></a> linked images as raw HTML so the image survives the round-trip.
// Without this rule, Turndown would emit [![alt](src)](href) which works for plain markdown
// but breaks when the link has a target attribute and mixes with the link-with-target rule.
// Keeping them as raw HTML means marked passes them through unchanged and the TipTap
// preprocessing step can reliably convert them to <linked-image> nodes.
// Added before link-with-target so it takes priority over that rule.
turndownService.addRule('linked-image', {
  filter: (node) => {
    if (node.nodeName !== 'A') return false;
    const el = node as HTMLElement;
    return el.children.length === 1 && el.children[0].nodeName === 'IMG';
  },
  replacement: (_content, node) => (node as HTMLElement).outerHTML,
});

// Preserve links with an explicit target as inline HTML so the attribute survives the round-trip.
// Added after preserveStyledHtml so it takes priority for links that also carry a class/style.
turndownService.addRule('link-with-target', {
  filter: (node) => node.nodeName === 'A' && !!(node as HTMLElement).getAttribute('target'),
  replacement: (content, node) => {
    const el = node as HTMLElement;
    const href = el.getAttribute('href') || '';
    const target = el.getAttribute('target') || '';
    return `<a href="${href}" target="${target}">${content}</a>`;
  }
});

// When <strong> or <em> contains inline HTML (e.g. <u>, <span>), output the whole element
// as raw HTML instead of wrapping with ** or _. Mixed markdown+HTML like **<u>text</u>**
// is not reliably parsed back by marked — the closing ** after </u> is misidentified as a
// new opener, causing literal asterisks and compounding \*\* escaping on every toggle.
// Added last so it takes priority over Turndown's built-in strong/em rules.
turndownService.addRule('emphasis-containing-html', {
  filter: (node) => {
    if (node.nodeType !== 1) return false;
    const el = node as HTMLElement;
    return (el.tagName === 'STRONG' || el.tagName === 'EM') && /<[a-z]/i.test(el.innerHTML);
  },
  replacement: (_content, node) => (node as HTMLElement).outerHTML,
});

// Strip <li><p> wrappers (TipTap adds them, causing extra bullet spacing) and normalize &nbsp;.
function normalizeHtml(html: string): string {
  return html.replace(/<li><p>/g, '<li>').replace(/<\/p><\/li>/g, '</li>').replace(/&nbsp;/g, ' ')
}

// Kept as a named alias used in htmlToMarkdown for clarity.
function stripListItemParagraphs(html: string): string {
  return normalizeHtml(html)
}

// Returns true when a stored value is already HTML (starts with '<').
export function looksLikeHtml(value: string): boolean {
  return value.trimStart().startsWith('<');
}
// Convert markdown to HTML for editing
export function markdownToHtml(markdown: string): string {
  if (!markdown) return '';
  // Per the CommonMark spec, an HTML block (type 6) started by a block-level tag
  // continues until the first blank line. Without a blank line between a closing tag
  // like </h2> and subsequent markdown (e.g. **bold**), marked treats the markdown as
  // part of the HTML block and passes the asterisks through literally. Inserting a blank
  // line after block-level closing tags prevents this.
  const normalized = markdown.replace(BLOCK_CLOSING_TAG_SINGLE_NEWLINE, '$1\n\n');
  return marked.parse(normalized, { async: false }) as string;
}

// Convert HTML to markdown for storage
export function htmlToMarkdown(html: string): string {
  if (!html) return '';
  return turndownService.turndown(stripListItemParagraphs(html));
}

// Matches a closing block-level tag followed by a single newline (not a blank line).
// Used by markdownToHtml to insert a blank line so marked exits HTML-block mode before
// processing any subsequent markdown. Hoisted to module scope — the g flag makes regex
// literals stateful, so keeping them inside functions would reconstruct them each call.
const BLOCK_CLOSING_TAG_SINGLE_NEWLINE = /(<\/(?:h[1-6]|div|p|ul|ol|table|blockquote|pre|figure|details|summary|section|article|header|footer|aside|nav|main|iframe)>)\n(?!\n)/gi;

// Block-level HTML elements: stored HTML starting with one of these can be used as-is.
// Inline-starting HTML (e.g. <strong>, <em>, <u> — output by the emphasis-containing-html
// Turndown rule) must go through markdownToHtml so TipTap gets a proper <p> block wrapper.
const BLOCK_ELEMENT_START = /^<(p|div|h[1-6]|ul|ol|li|table|thead|tbody|tr|td|th|blockquote|pre|figure|details|summary|section|article|header|footer|aside|nav|main|hr[ >\/])/i;

// Converts a stored value to HTML for TipTap. Handles four cases:
//   1. Pure markdown (old storage format) → markdownToHtml
//   2. Inline-starting HTML (e.g. Turndown output "<strong><u>…</u></strong>") →
//      markdownToHtml so marked wraps it in a block <p> element
//   3. Legacy mixed HTML+markdown (HTML with embedded markdown headings/lists) →
//      preprocess to fix structure, then markdownToHtml
//   4. Pure HTML (new storage format) → use as-is
// markdownToHtml output is trimmed to remove the trailing newline marked appends,
// keeping HTML values consistent with TipTap's getHTML() output (no trailing newline).
export function loadAsHtml(value: string): string {
  if (!value) return '';
  if (!looksLikeHtml(value) || !BLOCK_ELEMENT_START.test(value.trimStart())) {
    return markdownToHtml(value).trimEnd();
  }
  // Detect embedded markdown: headings ("\n# foo") or any non-HTML content after a blank
  // line ("\n\n**bold**", "\n\n_italic_", etc.). In pure-HTML storage TipTap never emits
  // blank lines between elements, so \n\n followed by a non-< non-whitespace char reliably
  // indicates markdown that needs markdownToHtml processing.
  if (!/\n#{1,6} /.test(value) && !/\n\n[^\s<]/.test(value) && !/<\/(?:h[1-6]|div|blockquote|ul|ol|table)>\n[^\s<]/i.test(value)) return value;
  // Legacy mixed format: strip bare <p> wrappers and add blank lines after
  // closing block tags so marked exits HTML-block mode and processes the markdown.
  const processed = value
    .replace(/<p>(\s)/g, '$1')
    .replace(/<\/p>/g, '\n')
    .replace(/(<\/(?:h[1-6]|div|blockquote|ul|ol|table)>)/gi, '$1\n\n')
    .replace(/\n{3,}/g, '\n\n');
  return markdownToHtml(processed).trimEnd();
}

// Converts <a href="..."><img ...></a> linked images into <linked-image> custom elements
// so TipTap's LinkedImageNode can parse them without conflicting with the Link mark's
// parseHTML rule for <a>. Uses string replacement (no DOM API) so there's no risk of
// browser serialization dropping attributes.
// LinkedImageNode.renderHTML outputs <a><img></a> again so storage stays as standard HTML.
function preprocessHtmlForTipTap(html: string): string {
  if (!html) return html;
  const getAttr = (str: string, name: string) =>
    str.match(new RegExp(`\\b${name}="([^"]*)"`, 'i'))?.[1] ?? null;
  // Matches <a ...>optional-whitespace<img .../>optional-whitespace</a>
  return html.replace(
    /<a(\s[^>]*)?\s*>\s*<img(\s[^>]*?)?\s*\/?>\s*<\/a>/gi,
    (_match, a: string = '', img: string = '') => {
      const pairs: [string, string | null][] = [
        ['href',           getAttr(a, 'href')],
        ['target',         getAttr(a, 'target')],
        ['data-src',       getAttr(img, 'src')],
        ['data-alt',       getAttr(img, 'alt')],
        ['data-img-style', getAttr(img, 'style')],
      ];
      const attrs = pairs.filter(([, v]) => v).map(([k, v]) => ` ${k}="${v}"`).join('');
      return `<linked-image${attrs}></linked-image>`;
    }
  );
}

/** Creates a standard TipTap attribute spec: reads `readAs` from the HTML element, renders as `name`. */
function htmlAttr(name: string, readAs = name) {
  return {
    default: null,
    parseHTML: (el: Element) => el.getAttribute(readAs),
    renderHTML: (a: Record<string, any>) => a[name] ? { [name]: a[name] } : {},
  };
}

// Preserves `style`, `class`, and `id` attributes on all known block/inline node types
// so that content with custom HTML attributes survives the TipTap schema round-trip.
const GlobalStyleAttributes = Extension.create({
  name: 'globalStyleAttributes',
  addGlobalAttributes() {
    return [
      {
        types: [
          'paragraph', 'heading', 'bulletList', 'orderedList', 'listItem',
          'blockquote', 'codeBlock',
          'table', 'tableRow', 'tableHeader', 'tableCell',
          'bold', 'italic', 'strike', 'code',
          'link', 'underline',
          'div', 'htmlBreak', 'htmlMark', 'span',
        ],
        attributes: {
          style: {
            default: null,
            parseHTML: el => el.getAttribute('style') || null,
            renderHTML: attrs => attrs.style ? { style: attrs.style } : {},
          },
          class: {
            default: null,
            parseHTML: el => el.getAttribute('class') || null,
            renderHTML: attrs => attrs.class ? { class: attrs.class } : {},
          },
          id: {
            default: null,
            parseHTML: el => el.getAttribute('id') || null,
            renderHTML: attrs => attrs.id ? { id: attrs.id } : {},
          },
        },
      },
    ];
  },
});

// Node that preserves <div> elements with their role and aria-live attributes,
// allowing accessible alert/notice blocks to survive the TipTap schema round-trip.
// style, class, and id are handled by GlobalStyleAttributes.
const DivNode = TipTapNode.create({
  name: 'div',
  group: 'block',
  content: 'block+',
  addAttributes() {
    return {
      role:        { default: null, parseHTML: el => el.getAttribute('role') || null,      renderHTML: attrs => attrs.role        ? { role: attrs.role }             : {} },
      'aria-live': { default: null, parseHTML: el => el.getAttribute('aria-live') || null, renderHTML: attrs => attrs['aria-live'] ? { 'aria-live': attrs['aria-live'] } : {} },
    };
  },
  parseHTML() {
    return [{ tag: 'div' }];
  },
  renderHTML({ HTMLAttributes }) {
    return ['div', mergeAttributes(HTMLAttributes), 0];
  },
});

// Block atom node for <div><br/></div> — represents an explicit blank line inserted by Enter.
// Uses priority 60 (> default 50) so its getAttrs filter runs before DivNode's catch-all rule.
const HtmlBreakNode = TipTapNode.create({
  name: 'htmlBreak',
  group: 'block',
  atom: true,
  parseHTML() {
    return [{
      tag: 'div',
      priority: 60,
      getAttrs: (dom) => isSingleBrDiv(dom as Element) ? {} : false,
    }];
  },
  renderHTML() {
    return ['div', {}, ['br', {}]];
  },
});

// Intercepts Enter on an empty top-level paragraph (i.e. double-Enter) to replace it
// with an HtmlBreakNode + new paragraph, producing a visible blank line. Single Enter
// in a non-empty paragraph falls through to TipTap's default split behavior (<p>/<p>).
const EnterKeyExtension = Extension.create({
  name: 'enterKey',
  addKeyboardShortcuts() {
    return {
      Enter: ({ editor }) => {
        const { state, dispatch } = editor.view;
        const { $from, empty } = state.selection;
        // Only intercept in paragraphs; let TipTap handle headings, list items, etc.
        if ($from.node().type.name !== 'paragraph') return false;
        // depth > 1 means inside a list item or blockquote — let TipTap handle those too.
        if ($from.depth > 1) return false;
        // Only intercept when the paragraph is empty (double-Enter scenario).
        // Non-empty paragraph: let TipTap split it normally into two <p> elements.
        if (!empty || $from.node().childCount !== 0) return false;

        const { schema } = state;
        const htmlBreakNode = schema.nodes.htmlBreak.create();
        const newParagraph = schema.nodes.paragraph.create();

        // Replace the empty paragraph with HtmlBreakNode + new empty paragraph.
        const tr = state.tr;
        const start = $from.before($from.depth);
        const end = $from.after($from.depth);
        tr.replaceWith(start, end, [htmlBreakNode, newParagraph]);
        // Place cursor inside the new paragraph. Atom block nodes have nodeSize=2
        // (open + close token), so start + nodeSize lands between the two nodes;
        // +1 steps past the new paragraph's opening token to the first text position.
        tr.setSelection(Selection.near(tr.doc.resolve(start + htmlBreakNode.nodeSize + 1)));

        dispatch(tr);
        return true;
      }
    };
  }
});

// Factory for inline marks that preserve an HTML tag through the TipTap schema round-trip.
// style, class, and id are handled by GlobalStyleAttributes.
function createInlineMark(name: string, tag: string) {
  return Mark.create({
    name,
    parseHTML() { return [{ tag }]; },
    renderHTML({ HTMLAttributes }) { return [tag, mergeAttributes(HTMLAttributes), 0]; },
  });
}

const MarkMark = createInlineMark('htmlMark', 'mark');
const SpanMark = createInlineMark('span', 'span');

// Inline atom node for <img> elements. StarterKit has no image support so without
// this, <img> tags are silently dropped when HTML loads into TipTap.
const ImageNode = TipTapNode.create({
  name: 'image',
  group: 'inline',
  inline: true,
  atom: true,
  addAttributes() {
    return {
      src:    htmlAttr('src'),
      alt:    htmlAttr('alt'),
      title:  htmlAttr('title'),
      width:  htmlAttr('width'),
      height: htmlAttr('height'),
      style:  htmlAttr('style'),
      class:  htmlAttr('class'),
    };
  },
  parseHTML() { return [{ tag: 'img[src]' }]; },
  renderHTML({ HTMLAttributes }) { return ['img', mergeAttributes(HTMLAttributes)]; },
});

// Inline atom node for <a><img></a> linked images. The Link mark applies to text content
// and cannot wrap an atom node, so linked images need their own node type to survive
// the TipTap schema round-trip.
const LinkedImageNode = TipTapNode.create({
  name: 'linkedImage',
  group: 'inline',
  inline: true,
  atom: true,
  // HTMLAttributes in renderHTML({HTMLAttributes}) is built from the MERGE of all
  // addAttributes().renderHTML() results — NOT from raw node attrs. So every attr that
  // needs to be read in renderHTML must be passed through via its own renderHTML function.
  addAttributes() {
    return {
      href:     htmlAttr('href'),
      target:   htmlAttr('target'),
      // data-src/data-alt/data-img-style are set by preprocessHtmlForTipTap on the
      // <linked-image> element; renderHTML passes them as their plain keys so they
      // are accessible as HTMLAttributes.src / .alt / .imgStyle in renderHTML below.
      src:      htmlAttr('src',      'data-src'),
      alt:      htmlAttr('alt',      'data-alt'),
      imgStyle: htmlAttr('imgStyle', 'data-img-style'),
    };
  },
  // Matches the <linked-image> custom element produced by preprocessHtmlForTipTap.
  // Using a custom tag avoids any conflict with the Link mark's <a> parse rule.
  parseHTML() {
    return [{ tag: 'linked-image' }];
  },
  renderHTML({ HTMLAttributes }) {
    const { href, target, src, alt, imgStyle } = HTMLAttributes;
    const imgAttrs: Record<string, any> = {};
    if (src) imgAttrs.src = src;
    if (alt) imgAttrs.alt = alt;
    if (imgStyle) imgAttrs.style = imgStyle;
    return ['a', { ...(href ? { href } : {}), ...(target ? { target } : {}) }, ['img', imgAttrs]];
  },
});

// Block atom node for <iframe> elements. TipTap's StarterKit has no iframe support,
// so without this <iframe> tags are silently dropped when HTML loads into TipTap.
// allowfullscreen is a boolean attribute: getAttribute returns "" or null, so its
// renderHTML uses != null rather than the truthy check that htmlAttr uses.
const IframeNode = TipTapNode.create({
  name: 'iframe',
  group: 'block',
  atom: true,
  addAttributes() {
    return {
      src:            htmlAttr('src'),
      width:          htmlAttr('width'),
      height:         htmlAttr('height'),
      frameborder:    htmlAttr('frameborder'),
      allow:          htmlAttr('allow'),
      allowfullscreen: {
        default: null,
        parseHTML: (el: Element) => el.getAttribute('allowfullscreen'),
        renderHTML: (a: Record<string, any>) => a['allowfullscreen'] != null ? { allowfullscreen: a['allowfullscreen'] } : {},
      },
      style:          htmlAttr('style'),
      class:          htmlAttr('class'),
      title:          htmlAttr('title'),
      loading:        htmlAttr('loading'),
      referrerpolicy: htmlAttr('referrerpolicy'),
      sandbox:        htmlAttr('sandbox'),
    };
  },
  parseHTML() { return [{ tag: 'iframe' }]; },
  renderHTML({ HTMLAttributes }) { return ['iframe', mergeAttributes(HTMLAttributes)]; },
});

// Block node for <button> elements. StarterKit has no button support, so without this
// <button> tags — and their onclick/other attributes — are silently unwrapped to plain
// text when HTML loads into TipTap. Content is inline (matching a real <button>'s text)
// and stays editable in the WYSIWYG view, the same way DivNode's children do.
const ButtonNode = TipTapNode.create({
  name: 'button',
  group: 'block',
  content: 'inline*',
  addAttributes() {
    return {
      onclick: htmlAttr('onclick'),
      type:    htmlAttr('type'),
      style:   htmlAttr('style'),
      class:   htmlAttr('class'),
      id:      htmlAttr('id'),
    };
  },
  parseHTML() { return [{ tag: 'button' }]; },
  renderHTML({ HTMLAttributes }) { return ['button', mergeAttributes(HTMLAttributes), 0]; },
});

export type RichTextEditorProps = {
  value: string;
  onChange: (html: string) => void;
  className?: string;
  contentClassName?: string;
  disabled?: boolean;
  onToggleMarkdown?: (currentHtml: string) => void;
  availableFields?: { key: string; label: string; }[];
  hideConditionalFormat?: boolean;
}

export function RichTextEditor({
  value,
  onChange,
  className = '',
  contentClassName = '',
  disabled = false,
  onToggleMarkdown,
  availableFields,
  hideConditionalFormat = false,
}: RichTextEditorProps) {
  const [showLinkInput, setShowLinkInput] = useState(false)
  const [linkUrl, setLinkUrl] = useState('')
  // Frozen at mount so useEditor's `content` option never changes between renders.
  const initialContentRef = useRef(preprocessHtmlForTipTap(value))
  // Tracks all emitted HTML (normalized) to avoid setContent on our own onChange echo-backs.
  const { markEmitted: markEmittedHtml, isEmitted: isEmittedHtml, resetTo: resetEmittedTo } = useEmittedValueTracker('')
  const savedSelection = useRef<{ from: number; to: number } | null>(null)
  const frozenSelectionRef = useRef<{ from: number; to: number } | null>(null)
  const [linkNewTab, setLinkNewTab] = useState(true)
  const linkHighlightExtension = useMemo(() => createLinkHighlightExtension(frozenSelectionRef), [])
  const [, forceUpdate] = useState(0)
  const [showFieldPicker, setShowFieldPicker] = useState(false)
  const [fieldSearch, setFieldSearch] = useState('')
  const filteredFields = useMemo(() => {
    const q = fieldSearch.toLowerCase()
    return (availableFields ?? []).filter(f =>
      f.label.toLowerCase().includes(q) || f.key.toLowerCase().includes(q)
    )
  }, [availableFields, fieldSearch])
  const fieldPickerRef = useRef<HTMLDivElement>(null)
  const [showConditionalFormat, setShowConditionalFormat] = useState(false)

  useEffect(() => {
    if (!availableFields || availableFields.length === 0) {
      setShowFieldPicker(false)
      setFieldSearch('')
    }
  }, [availableFields])

  useEffect(() => {
    if (!showFieldPicker) return
    const handleClickOutside = (e: MouseEvent) => {
      if (fieldPickerRef.current && !fieldPickerRef.current.contains(e.target as Node)) {
        setShowFieldPicker(false)
        setFieldSearch('')
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [showFieldPicker])

  const editor = useEditor({
    extensions: [
      StarterKit.configure({
        heading: {
          levels: [1, 2, 3, 4, 5]
        },
        // Disabled because we add our own configured versions below.
        // TipTap v3 StarterKit now bundles these by default, causing duplicate warnings.
        link: false,
        underline: false,
      }),
      Underline,
      LinkedImageNode,
      ImageNode,
      IframeNode,
      Link.configure({
        openOnClick: false,
        HTMLAttributes: {
          rel: 'noopener noreferrer',
        },
      }),
      linkHighlightExtension,
      Table.configure({ resizable: true, cellMinWidth: 100 }),
      TableRow,
      CustomTableHeader,
      TableCell,
      GlobalStyleAttributes,
      SpanMark,
      MarkMark,
      HtmlBreakNode,
      DivNode,
      ButtonNode,
      EnterKeyExtension,
      ConditionalFormattingExtension,
    ],
    content: initialContentRef.current,
    editable: !disabled,
    onUpdate: ({ editor }) => {
      const nextHtml = stripListItemParagraphs(editor.getHTML()).replace(/<p><\/p>$/, '')
      if (nextHtml === value) return
      markEmittedHtml(normalizeHtml(nextHtml))
      onChange(nextHtml)
    },
    onSelectionUpdate: () => forceUpdate(n => n + 1),
    editorProps: {
      transformPastedHTML: stripPastedFontStyles,
      attributes: {
        class: cn('rich-text-content focus:outline-none min-h-[75px] p-3', contentClassName)
      }
    }
  })

  // Sync external value changes into the editor, skipping stale echo-backs of our own edits.
  useEffect(() => {
    if (!editor) return
    const normValue = normalizeHtml(value).replace(/<p><\/p>$/, '')
    const normEditor = normalizeHtml(editor.getHTML()).replace(/<p><\/p>$/, '')
    if (normValue === normEditor) return

    // Incoming value is our own echo-back — skip to avoid clobbering in-progress edits.
    // Exception: empty string means we haven't emitted yet, so allow initial loads.
    if (isEmittedHtml(normValue) && normValue !== '') return

    // Genuine external change — preserve cursor position across the full document replace.
    resetEmittedTo(normValue)
    const { from } = editor.state.selection
    editor.commands.setContent(preprocessHtmlForTipTap(value), { emitUpdate: false })
    const { doc, tr } = editor.state
    const clampedPos = Math.min(from, Math.max(1, doc.content.size - 1))
    const $pos = doc.resolve(clampedPos)
    editor.view.dispatch(tr.setSelection(Selection.near($pos)))
  }, [value, editor])

  if (!editor) {
    return null
  }

  const closeLinkPanel = () => {
    frozenSelectionRef.current = null
    editor.view.dispatch(editor.state.tr)
    savedSelection.current = null
    setShowLinkInput(false)
    setLinkUrl('')
  }

  const openLinkInput = () => {
    const { from, to } = editor.state.selection
    savedSelection.current = { from, to }
    frozenSelectionRef.current = { from, to }
    editor.view.dispatch(editor.state.tr)
    const attrs = editor.getAttributes('link')
    setLinkUrl(attrs.href || '')
    setLinkNewTab(attrs.target !== '_self')
    setShowLinkInput(true)
  }

  const applyLink = () => {
    if (linkUrl) {
      editor.view.focus()
      const chain = editor.chain()
      if (savedSelection.current) {
        chain.setTextSelection(savedSelection.current)
      }
      chain.extendMarkRange('link').setLink({ href: linkUrl, target: linkNewTab ? '_blank' : '_self' }).run()
    }
    closeLinkPanel()
  }

  const removeLink = () => {
    editor.chain().focus().unsetLink().run()
    closeLinkPanel()
  }

  return (
    <div className={cn('border border-gray-300 rounded-md bg-white', className)}>
      {/* Toolbar */}
      <div className="border-b border-gray-300 p-2 flex flex-wrap gap-1 bg-gray-50 rounded-t-md">
        <ToolbarButton
          onClick={() => editor.chain().focus().undo().run()}
          active={false}
          disabled={disabled || !editor.can().undo()}
          title="Undo"
        >
          <Icon name="ArrowUturnLeftIcon" className="h-4 w-4" />
        </ToolbarButton>

        <ToolbarButton
          onClick={() => editor.chain().focus().redo().run()}
          active={false}
          disabled={disabled || !editor.can().redo()}
          title="Redo"
        >
          <Icon name="ArrowUturnRightIcon" className="h-4 w-4" />
        </ToolbarButton>

        <ToolbarButton
          onClick={() => editor.chain().focus().toggleBold().run()}
          active={editor.isActive('bold')}
          disabled={disabled}
          title="Bold"
        >
          <span className="text-sm font-bold">B</span>
        </ToolbarButton>

        <ToolbarButton
          onClick={() => editor.chain().focus().toggleItalic().run()}
          active={editor.isActive('italic')}
          disabled={disabled}
          title="Italic"
        >
          <span className="text-sm italic">I</span>
        </ToolbarButton>

        <ToolbarButton
          onClick={() => editor.chain().focus().toggleUnderline().run()}
          active={editor.isActive('underline')}
          disabled={disabled}
          title="Underline"
        >
          <span className="text-sm underline">U</span>
        </ToolbarButton>

        <div className="w-px bg-gray-300 mx-1" />

        <ToolbarButton
          onClick={() => editor.chain().focus().setParagraph().run()}
          active={editor.isActive('paragraph')}
          disabled={disabled}
          title="Paragraph"
        >
          <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" viewBox="0 0 24 24" fill="currentColor">
            <path d="M6 3h6v9H6a4.5 4.5 0 0 1 0-9z"/>
            <rect x="10" y="3" width="2" height="18"/>
            <rect x="14" y="3" width="2" height="18"/>
          </svg>
        </ToolbarButton>

        <ToolbarButton
          onClick={() => editor.chain().focus().toggleHeading({ level: 1 }).run()}
          active={editor.isActive('heading', { level: 1 })}
          disabled={disabled}
          title="Heading 1"
        >
          <span className="text-sm font-bold">H1</span>
        </ToolbarButton>

        <ToolbarButton
          onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()}
          active={editor.isActive('heading', { level: 2 })}
          disabled={disabled}
          title="Heading 2"
        >
          <span className="text-sm font-bold">H2</span>
        </ToolbarButton>

        <ToolbarButton
          onClick={() => editor.chain().focus().toggleHeading({ level: 3 }).run()}
          active={editor.isActive('heading', { level: 3 })}
          disabled={disabled}
          title="Heading 3"
        >
          <span className="text-sm font-bold">H3</span>
        </ToolbarButton>

        <ToolbarButton
          onClick={() => editor.chain().focus().toggleHeading({ level: 4 }).run()}
          active={editor.isActive('heading', { level: 4 })}
          disabled={disabled}
          title="Heading 4"
        >
          <span className="text-sm font-bold">H4</span>
        </ToolbarButton>

        <ToolbarButton
          onClick={() => editor.chain().focus().toggleHeading({ level: 5 }).run()}
          active={editor.isActive('heading', { level: 5 })}
          disabled={disabled}
          title="Heading 5"
        >
          <span className="text-sm font-bold">H5</span>
        </ToolbarButton>

        <div className="w-px bg-gray-300 mx-1" />

        <ToolbarButton
          onClick={() => editor.chain().focus().toggleBulletList().run()}
          active={editor.isActive('bulletList')}
          disabled={disabled}
          title="Bullet List"
        >
          <Icon name="ListBulletIcon" className="h-4 w-4" />
        </ToolbarButton>

        <ToolbarButton
          onClick={() => editor.chain().focus().toggleOrderedList().run()}
          active={editor.isActive('orderedList')}
          disabled={disabled}
          title="Numbered List"
        >
          <Icon name="NumberedListIcon" className="h-4 w-4" />
        </ToolbarButton>

        <ToolbarButton
          onClick={() => editor.chain().focus().setHorizontalRule().run()}
          active={false}
          disabled={disabled}
          title="Horizontal Rule"
        >
          <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
            <line x1="3" y1="12" x2="21" y2="12" />
          </svg>
        </ToolbarButton>

        <div className="w-px bg-gray-300 mx-1" />

        <ToolbarButton
          onClick={openLinkInput}
          active={editor.isActive('link')}
          disabled={disabled}
          title="Add Link"
        >
          <Icon name="LinkIcon" className="h-4 w-4" />
        </ToolbarButton>

        {availableFields && availableFields.length > 0 && (
          <div className="relative" ref={fieldPickerRef}>
            <ToolbarButton
              onClick={() => { setShowFieldPicker(v => !v); setFieldSearch('') }}
              active={showFieldPicker}
              disabled={disabled}
              title="Insert field reference"
            >
              <span className="text-sm whitespace-nowrap flex items-center gap-1">Field Value<Icon name="ChevronDownIcon" className="h-3 w-3" /></span>
            </ToolbarButton>
            {showFieldPicker && (
              <div className="absolute z-50 top-full left-0 mt-1 w-64 bg-white border border-gray-200 rounded-md shadow-lg">
                <input
                  type="text"
                  value={fieldSearch}
                  onChange={e => setFieldSearch(e.target.value)}
                  placeholder="Search fields..."
                  className="w-full px-3 py-2 text-sm border-b border-gray-200 focus:outline-none rounded-t-md"
                  autoFocus
                />
                <ul className="max-h-52 overflow-y-auto py-1">
                  {filteredFields.map(field => (
                    <li key={field.key}>
                      <button
                        type="button"
                        onMouseDown={e => {
                          e.preventDefault()
                          editor.chain().focus().insertContent({ type: 'text', text: `$${field.key}` }).run()
                          setShowFieldPicker(false)
                          setFieldSearch('')
                        }}
                        className="w-full text-left px-3 py-1.5 text-sm hover:bg-gray-100 flex items-baseline gap-2"
                      >
                        <span>{field.label}</span>
                      </button>
                    </li>
                  ))
                  }
                  {filteredFields.length === 0 && (
                    <li className="px-3 py-2 text-sm text-gray-400">No fields found</li>
                  )}
                </ul>
              </div>
            )}
          </div>
        )}

        <ToolbarButton
          onClick={() => {
            const inserted = editor.chain().focus().insertTable({ rows: 3, cols: 3, withHeaderRow: true }).run();
            // Stamp the newly inserted table with class='tiptap-table' so it can be
            // distinguished from legacy tables when rendering.
            if (inserted) {
              const { state, view } = editor;
              const { $head } = state.selection;
              for (let d = $head.depth; d >= 0; d--) {
                if ($head.node(d).type.name === 'table') {
                  const node = $head.node(d);
                  view.dispatch(state.tr.setNodeMarkup($head.before(d), undefined, { ...node.attrs, class: 'tiptap-table' }));
                  break;
                }
              }
            }
          }}
          active={editor.isActive('table')}
          disabled={disabled}
          title="Insert Table"
        >
          <Icon name="TableCellsIcon" className="h-4 w-4" />
        </ToolbarButton>

        {onToggleMarkdown && (
          <div className="ml-auto flex items-center">
            <ToolbarButton
              onClick={() => onToggleMarkdown(editor.getHTML())}
              active={false}
              disabled={false}
              title="Switch to markdown view"
            >
              <Icon name="CodeBracketIcon" className="h-5 w-5" type="solid" />
            </ToolbarButton>
          </div>
        )}
      </div>

      {/* Table controls — shown when cursor is inside a table */}
      {editor.isActive('table') && !disabled && (
        <div className="border-b border-gray-300 px-2 py-1.5 flex flex-wrap items-center gap-1 bg-gray-50 text-xs text-gray-600">
          <Button type="button" variant="secondary" size="sm" onClick={() => editor.chain().focus().addColumnBefore().run()}>+ Col Left</Button>
          <Button type="button" variant="secondary" size="sm" onClick={() => editor.chain().focus().addColumnAfter().run()}>+ Col Right</Button>
          <Button type="button" variant="secondary" size="sm" onClick={() => editor.chain().focus().deleteColumn().run()}>- Col</Button>
          <div className="w-px bg-gray-300 mx-1 self-stretch" />
          <Button type="button" variant="secondary" size="sm" onClick={() => editor.chain().focus().addRowBefore().run()}>+ Row Above</Button>
          <Button type="button" variant="secondary" size="sm" onClick={() => editor.chain().focus().addRowAfter().run()}>+ Row Below</Button>
          <Button type="button" variant="secondary" size="sm" onClick={() => editor.chain().focus().deleteRow().run()}>- Row</Button>
          <div className="w-px bg-gray-300 mx-1 self-stretch" />
          <Button type="button" variant="danger" size="sm" onClick={() => editor.chain().focus().deleteTable().run()}>Delete Table</Button>
          {!hideConditionalFormat && (
            <>
              <div className="w-px bg-gray-300 mx-1 self-stretch" />
              <Button type="button" variant="secondary" size="sm" onClick={() => setShowConditionalFormat(true)}>
                <span className="flex items-center gap-1">
                  <Icon name="SparklesIcon" className="h-3.5 w-3.5" />
                  Format
                </span>
              </Button>
              {(() => { const n = getTotalRuleCount(editor); return n > 0 ? <span className="text-xs text-indigo-600 font-medium">{n} {n === 1 ? 'rule' : 'rules'} set</span> : null; })()}
            </>
          )}
        </div>
      )}

      {/* Conditional formatting modal */}
      {showConditionalFormat && (
        <ConditionalFormatModal
          editor={editor}
          onClose={() => setShowConditionalFormat(false)}
        />
      )}

      {/* Link input row */}
      {showLinkInput && (
        <div className="border-b border-gray-300 px-2 py-1.5 flex flex-wrap items-center gap-2 bg-gray-50">
          <input
            type="url"
            value={linkUrl}
            onChange={(e) => setLinkUrl(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') applyLink()
              if (e.key === 'Escape') closeLinkPanel()
            }}
            placeholder="https://..."
            className="flex-1 text-sm border border-gray-300 rounded px-2 py-1 focus:outline-none focus:ring-1 focus:ring-blue-500"
          />
          <Checkbox
            label="New tab"
            checked={linkNewTab}
            onChange={setLinkNewTab as any} // Checkbox calls onChange(boolean) directly; setState accepts boolean
          />
          <Button type="button" variant="primary" size="sm" onClick={applyLink}>
            Apply
          </Button>
          {editor.isActive('link') && (
            <Button type="button" variant="danger" size="sm" onClick={removeLink}>
              Remove
            </Button>
          )}
          <Button type="button" variant="secondary" size="sm" onClick={closeLinkPanel}>
            Cancel
          </Button>
        </div>
      )}

      {/* Editor Content */}
      <EditorContent editor={editor} />
    </div>
  )
}

function ToolbarButton({
  onClick,
  active,
  disabled,
  title,
  children
}: {
  onClick: () => void;
  active: boolean;
  disabled?: boolean;
  title: string;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      onMouseDown={e => e.preventDefault()}
      disabled={disabled}
      title={title}
      className={cn(
        'p-2 rounded hover:bg-gray-200 transition-colors',
        active && 'bg-gray-300',
        disabled && 'opacity-50 cursor-not-allowed'
      )}
    >
      {children}
    </button>
  )
}

export function MarkdownSourceView({
  sourceContent,
  onChange,
  onToggle,
  disabled,
}: {
  sourceContent: string;
  onChange: (e: React.ChangeEvent<HTMLTextAreaElement>) => void;
  onToggle: () => void;
  disabled?: boolean;
}) {
  return (
    <div className="border border-gray-300 rounded-md bg-white">
      <div className="border-b border-gray-300 p-2 flex bg-gray-50">
        <button
          type="button"
          onClick={onToggle}
          title="Switch to formatted view"
          className="ml-auto p-2 rounded hover:bg-gray-200 transition-colors bg-gray-300"
        >
          <Icon name="DocumentTextIcon" className="h-5 w-5" type="solid" />
        </button>
      </div>
      <textarea
        value={sourceContent}
        onChange={onChange}
        disabled={disabled}
        spellCheck={false}
        placeholder="Enter markdown or HTML..."
        className="w-full min-h-[150px] p-3 font-mono text-sm focus:outline-none resize-y bg-white rounded-b-md disabled:opacity-50 disabled:cursor-not-allowed"
      />
    </div>
  );
}

// Tracks all values emitted by a component so stale collab echoes (e.g. a debounce firing
// for "Hello" after the user has typed ahead to "Hello World") are not mistaken for genuine
// external updates. Call markEmitted() when sending a value, isEmitted() to test an incoming
// value, and resetTo() when a genuine external update is accepted.
const MAX_EMITTED_SIZE = 100;

export function useEmittedValueTracker(initialValue: string) {
  const setRef = useRef(new Set<string>([initialValue]));
  const markEmitted = useCallback((value: string) => {
    if (!setRef.current.has(value) && setRef.current.size >= MAX_EMITTED_SIZE) {
      const oldest = setRef.current.values().next().value;
      if (oldest !== undefined) setRef.current.delete(oldest);
    }
    setRef.current.add(value);
  }, []);
  const isEmitted = useCallback((value: string) => setRef.current.has(value), []);
  const resetTo = useCallback((value: string) => { setRef.current = new Set<string>([value]); }, []);
  return { markEmitted, isEmitted, resetTo };
}

function useRichTextToggle(value: string, onSave: (html: string) => void) {
  const [viewMarkdown, setViewMarkdown] = useState(false);
  const [htmlContent, setHtmlContent] = useState(value);
  const [sourceContent, setSourceContent] = useState(() => htmlToMarkdown(value));
  const { markEmitted, isEmitted, resetTo } = useEmittedValueTracker(value);
  // Tracks the latest committed sourceContent without being a dependency of the effect below.
  // Used to avoid calling setSourceContent (and jumping the textarea cursor) when the
  // incoming HTML converts back to the same markdown the user already has in the source view.
  const sourceContentRef = useRef(sourceContent);
  sourceContentRef.current = sourceContent;

  useEffect(() => {
    if (isEmitted(value)) return;
    // Secondary guard: if the current source content renders to the same HTML as the
    // incoming value, it's semantically an echo of our own typing (e.g. markdown soft
    // line breaks → HTML spaces → different string but same rendered output). Skip all
    // updates to prevent cursor jump and Turndown-escaping artifacts.
    if (loadAsHtml(sourceContentRef.current) === value) return;
    // Genuine external update — reset tracking and sync UI.
    resetTo(value);
    setHtmlContent(value);
    const newMarkdown = htmlToMarkdown(value);
    if (newMarkdown !== sourceContentRef.current) {
      setSourceContent(newMarkdown);
    }
  }, [value]);

  const handleRichTextChange = useCallback((html: string) => {
    markEmitted(html);
    setHtmlContent(html);
    onSave(html);
  }, [onSave]);

  const handleSourceChange = useCallback((e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const src = e.target.value;
    setSourceContent(src);
    const html = loadAsHtml(src);
    markEmitted(html);
    onSave(html);
  }, [onSave]);

  const handleToggleMarkdown = useCallback((currentHtml?: string) => {
    if (!viewMarkdown) {
      setSourceContent(htmlToMarkdown(currentHtml ?? htmlContent));
    } else {
      const html = loadAsHtml(sourceContent);
      setHtmlContent(html);
      if (html !== htmlContent) {
        markEmitted(html);
        onSave(html);
      }
    }
    setViewMarkdown(v => !v);
  }, [viewMarkdown, htmlContent, sourceContent, onSave]);

  return { viewMarkdown, htmlContent, setHtmlContent, sourceContent, setSourceContent,
    handleRichTextChange, handleSourceChange, handleToggleMarkdown };
}

export function RichTextEditorWithToggle({
  value,
  onChange,
  disabled = false,
  className,
  contentClassName,
}: {
  value: string;
  onChange: (html: string) => void;
  disabled?: boolean;
  className?: string;
  contentClassName?: string;
}) {
  const { viewMarkdown, htmlContent, sourceContent, handleRichTextChange, handleSourceChange, handleToggleMarkdown }
    = useRichTextToggle(loadAsHtml(value), onChange);

  return viewMarkdown
    ? <MarkdownSourceView sourceContent={sourceContent} onChange={handleSourceChange} onToggle={() => handleToggleMarkdown()} disabled={disabled} />
    : <RichTextEditor value={htmlContent} onChange={handleRichTextChange} disabled={disabled} className={className} contentClassName={contentClassName} onToggleMarkdown={handleToggleMarkdown} />;
}

// Conditional formatting types & constants

type ConditionOp = 'lt' | 'lte' | 'gt' | 'gte' | 'eq' | 'neq' | 'contains' | 'else'

type StoredConditionalRule = {
  op: ConditionOp
  value: string
  color: string
}

// Local React state adds a stable `id` for list keys
type ConditionalRule = StoredConditionalRule & { id: string }

const OP_OPTIONS: Array<{ op: Exclude<ConditionOp, 'else'>; label: string }> = [
  { op: 'lt', label: '<' },
  { op: 'lte', label: '≤' },
  { op: 'gt', label: '>' },
  { op: 'gte', label: '≥' },
  { op: 'eq', label: '=' },
  { op: 'neq', label: '≠' },
  { op: 'contains', label: 'contains' },
]

const COLOR_PRESETS = [
  { label: 'Red',   bg: '#fee2e2' },
  { label: 'Green', bg: '#dcfce7' },
  { label: 'Blue',  bg: '#dbeafe' },
  { label: 'Gray',  bg: '#f3f4f6' },
  { label: 'None',  bg: '' },
]

function newRule(op: ConditionOp = 'lt'): ConditionalRule {
  return { id: Math.random().toString(36).slice(2, 8), op, value: '', color: '' }
}


const conditionalFormattingKey = new PluginKey('conditionalFormatting')

const ConditionalFormattingExtension = Extension.create({
  name: 'conditionalFormatting',
  addProseMirrorPlugins() {
    return [
      new Plugin({
        key: conditionalFormattingKey,
        props: {
          decorations(state) {
            const decorations: Decoration[] = []
            state.doc.descendants((node, pos) => {
              if (node.type.name !== 'table') return
              // Collect per-column rules from the header row
              const columnRules: Array<StoredConditionalRule[] | null> = []
              const firstRow = node.firstChild
              if (!firstRow) return false
              firstRow.forEach((cell) => {
                if (cell.attrs?.dataConditionalRules) {
                  try { columnRules.push(JSON.parse(cell.attrs.dataConditionalRules)) }
                  catch { columnRules.push(null) }
                } else {
                  columnRules.push(null)
                }
              })
              if (columnRules.every(r => !r)) return false
              // Apply decorations to data rows (skip row index 0 = header)
              node.forEach((row, rowOffset, rowIndex) => {
                if (rowIndex === 0) return
                const rowPos = pos + 1 + rowOffset
                row.forEach((cell, cellOffset, colIndex) => {
                  const rules = columnRules[colIndex]
                  if (!rules?.length) return
                  const color = evaluateConditionalRules(rules, cell.textContent.trim())
                  if (color) {
                    const cellPos = rowPos + 1 + cellOffset
                    decorations.push(Decoration.node(cellPos, cellPos + cell.nodeSize, {
                      style: `background-color: ${color};`
                    }))
                  }
                })
              })
              return false
            })
            return DecorationSet.create(state.doc, decorations)
          }
        }
      })
    ]
  }
})

function findTableContext(editor: Editor): { tableNode: ReturnType<typeof editor.state.doc.child>, tablePos: number, firstRow: ReturnType<typeof editor.state.doc.child> } | null {
  const { $from } = editor.state.selection
  for (let d = $from.depth; d >= 0; d--) {
    if ($from.node(d).type.name === 'table') {
      const tableNode = $from.node(d)
      if (tableNode.childCount === 0) return null
      const firstRow = tableNode.child(0)
      if (firstRow.type.name !== 'tableRow') return null
      return { tableNode, tablePos: $from.before(d), firstRow }
    }
  }
  return null
}

function getColumnConfig(editor: Editor, colIndex: number): { rules: ConditionalRule[] } {
  const ctx = findTableContext(editor)
  if (!ctx || colIndex >= ctx.firstRow.childCount) return { rules: [] }
  const cell = ctx.firstRow.child(colIndex)
  let rules: ConditionalRule[] = []
  try {
    const raw: StoredConditionalRule[] = cell.attrs.dataConditionalRules
      ? JSON.parse(cell.attrs.dataConditionalRules)
      : []
    rules = raw.map(r => ({ ...r, id: Math.random().toString(36).slice(2, 8) }))
  } catch {}
  return { rules }
}

function saveColumnRules(editor: Editor, colIndex: number, rules: StoredConditionalRule[], tr?: Transaction): Transaction | undefined {
  const ctx = findTableContext(editor)
  if (!ctx || colIndex >= ctx.firstRow.childCount) return tr
  // tablePos+1 = into table, +1 = into first row
  let cellPos = ctx.tablePos + 2
  for (let i = 0; i < colIndex; i++) cellPos += ctx.firstRow.child(i).nodeSize
  const cell = ctx.firstRow.child(colIndex)
  const baseTr = tr ?? editor.state.tr
  return baseTr.setNodeMarkup(cellPos, null, {
    ...cell.attrs,
    dataConditionalRules: rules.length > 0 ? JSON.stringify(rules) : null,
  })
}

function getTotalRuleCount(editor: Editor): number {
  const ctx = findTableContext(editor)
  if (!ctx) return 0
  let total = 0
  for (let i = 0; i < ctx.firstRow.childCount; i++) {
    const cell = ctx.firstRow.child(i)
    if (cell.attrs?.dataConditionalRules) {
      try { total += JSON.parse(cell.attrs.dataConditionalRules).length } catch {}
    }
  }
  return total
}

function getTableHeaders(editor: Editor): string[] {
  const ctx = findTableContext(editor)
  if (!ctx) return []
  const headers: string[] = []
  for (let i = 0; i < ctx.firstRow.childCount; i++) {
    headers.push(ctx.firstRow.child(i).textContent || `Column ${i + 1}`)
  }
  return headers
}

function RuleRow({ rule, onChange, onRemove }: {
  rule: ConditionalRule
  onChange: (patch: Partial<ConditionalRule>) => void
  onRemove: () => void
}) {
  return (
    <div className="flex items-center gap-2 bg-gray-50 rounded-md px-3 py-2 border border-gray-200">
      {rule.op === 'else' ? (
        <span className="text-sm font-bold">Otherwise</span>
      ) : (
        <>
          <span className="text-sm font-bold">Value</span>
          <select
            value={rule.op}
            onChange={e => onChange({ op: e.target.value as ConditionOp })}
            className="text-sm border border-gray-200 rounded px-1.5 py-1 focus:outline-none focus:ring-1 focus:ring-indigo-400 bg-white"
          >
            {OP_OPTIONS.map(({ op, label }) => (
              <option key={op} value={op}>{label}</option>
            ))}
          </select>
          <input
            type="text"
            value={rule.value}
            onChange={e => onChange({ value: e.target.value })}
            placeholder="value"
            className="w-20 text-sm border border-gray-200 rounded px-2 py-1 focus:outline-none focus:ring-1 focus:ring-indigo-400"
          />
        </>
      )}
      <div className="flex gap-1 ml-auto">
        {COLOR_PRESETS.map(({ label, bg }) => (
          <button
            key={label}
            type="button"
            title={label}
            onClick={() => onChange({ color: bg })}
            className={cn(
              'w-5 h-5 rounded-full border-2 transition-transform hover:scale-110',
              rule.color === bg ? 'border-gray-700 scale-110' : 'border-transparent',
            )}
            style={bg
              ? { backgroundColor: bg }
              : { background: 'repeating-linear-gradient(45deg,#e5e7eb,#e5e7eb 2px,white 2px,white 6px)', border: '1px solid #d1d5db' }
            }
          />
        ))}
      </div>
      <button
        type="button"
        onClick={onRemove}
        className="text-gray-300 hover:text-red-400 transition-colors ml-1 shrink-0"
      >
        <Icon name="XMarkIcon" className="h-4 w-4" />
      </button>
    </div>
  )
}

function ConditionalFormatModal({ editor, onClose }: {
  editor: Editor
  onClose: () => void
}) {
  const headers = useMemo(() => getTableHeaders(editor), [editor])
  const [selectedCol, setSelectedCol] = useState<number | null>(headers.length === 1 ? 0 : null)
  // pendingRules holds unsaved edits per column index until Save is clicked
  const [pendingRules, setPendingRules] = useState<Record<number, ConditionalRule[]>>(() =>
    headers.length === 1 ? { 0: getColumnConfig(editor, 0).rules } : {} as Record<number, ConditionalRule[]>
  )
  const [isSaving, setIsSaving] = useState(false)

  const rules = selectedCol !== null ? (pendingRules[selectedCol] ?? []) : []

  const setRules = (updater: ConditionalRule[] | ((prev: ConditionalRule[]) => ConditionalRule[])) => {
    if (selectedCol === null) return
    setPendingRules(prev => {
      const current = prev[selectedCol] ?? []
      const next = typeof updater === 'function' ? updater(current) : updater
      return { ...prev, [selectedCol]: next }
    })
  }

  const handleColSelect = (i: number) => {
    setSelectedCol(i)
    setPendingRules(prev => {
      if (i in prev) return prev
      return { ...prev, [i]: getColumnConfig(editor, i).rules }
    })
  }

  const updateRule = (id: string, patch: Partial<ConditionalRule>) =>
    setRules(prev => prev.map(r => r.id === id ? { ...r, ...patch } : r))

  const removeRule = (id: string) =>
    setRules(prev => prev.filter(r => r.id !== id))

  const addRule = () =>
    setRules(prev => [
      ...prev.filter(r => r.op !== 'else'),
      newRule('lt'),
      ...prev.filter(r => r.op === 'else'),
    ])

  const addOtherwise = () => {
    if (!rules.some(r => r.op === 'else')) {
      setRules(prev => [...prev, newRule('else')])
    }
  }

  const handleSave = () => {
    setIsSaving(true)
    requestAnimationFrame(() => {
      let tr: Transaction | undefined
      for (const [colStr, colRules] of Object.entries(pendingRules)) {
        const stored: StoredConditionalRule[] = colRules.map(({ id: _id, ...r }) => r)
        tr = saveColumnRules(editor, Number(colStr), stored, tr)
      }
      if (tr) editor.view.dispatch(tr)
      onClose()
    })
  }

  const hasOtherwise = rules.some(r => r.op === 'else')

  return (
    <Modal
      open={true}
      onClose={() => onClose()}
      title="Conditional Cell Colors"
      size="lg"
      icon={<Icon name="SparklesIcon" className="h-5 w-5" />}
    >
      <div className="mt-3">
        {headers.length === 0 ? (
          <p className="text-sm text-gray-500">No header row found. Add a table with a header row to use conditional formatting.</p>
        ) : (
          <>
            <div className="mb-2">
              <label className="text-sm font-bold">
                Choose a column to evaluate
              </label>
              <div className="flex flex-wrap gap-2">
                {headers.map((header, i) => (
                  <Button
                    key={i}
                    type="button"
                    variant={selectedCol === i ? 'primary' : 'secondary'}
                    onClick={() => handleColSelect(i)}
                  >
                    {header}
                  </Button>
                ))}
              </div>
            </div>

            {selectedCol !== null && (
              <div>
                <label className="text-sm font-bold">
                  Set formatting rules
                </label>

                {rules.length === 0 && (
                  <p className="text-sm text-gray-400 italic mb-3">No rules yet — add a condition or an "Otherwise" default below.</p>
                )}

                <div className="space-y-2 mb-3">
                  {rules.map(rule => (
                    <RuleRow
                      key={rule.id}
                      rule={rule}
                      onChange={patch => updateRule(rule.id, patch)}
                      onRemove={() => removeRule(rule.id)}
                    />
                  ))}
                </div>

                <div className="flex gap-2">
                  <Button type="button" variant="secondary" size="sm" onClick={addRule}
                    icon={<Icon name="PlusIcon" className="h-3.5 w-3.5" />}>
                    Add Rule
                  </Button>
                  {!hasOtherwise && (
                    <Button type="button" variant="secondary" size="sm" onClick={addOtherwise}
                      icon={<Icon name="PlusIcon" className="h-3.5 w-3.5" />}>
                      Otherwise
                    </Button>
                  )}
                  {rules.length > 0 && (
                    <Button type="button" variant="danger" size="sm" onClick={() => setRules([])}>
                      Clear all
                    </Button>
                  )}
                </div>
              </div>
            )}

            <div className="flex justify-end gap-2 mt-2 pt-4 border-t border-gray-200">
              <Button type="button" variant="secondary" onClick={onClose}>Cancel</Button>
              {Object.keys(pendingRules).length > 0 && (
                <Button type="button" variant="primary" onClick={handleSave} isDisabled={isSaving}>
                  <span className="flex items-center gap-1.5">
                    {isSaving && <Spinner size="xsmall" className="inline-flex !w-auto p-0" />}
                    {isSaving ? 'Saving' : 'Save'}
                  </span>
                </Button>
              )}
            </div>
          </>
        )}
      </div>
    </Modal>
  )
}

export function RichTextEditorComponent(props: NodeProps) {
  const richTextObject = props.trackedObject as TrackedRecord;

  // Schema-level language definitions (all possible langs, minus hidden meta keys)
  const nodeProps = (props.node as any)?.props ?? {};
  const nodePropMeta = (props.node as any)?.propMeta ?? {};
  const requiredLangs: string[] = ((props.node as any)?.required ?? []).filter((k: string) => k !== 'kind');
  const schemaLangs: string[] = Object.keys(nodeProps).filter(k => k !== 'kind' && nodePropMeta[k]?.hidden !== 'yes');

  // Currently added language keys (from tracked object)
  const languages = Object.keys(richTextObject.value).filter(k => k !== '_id' && k !== 'kind');

  // Optional langs available to add (in schema, not yet in tracked object)
  const unusedOptionalLangs = schemaLangs.filter(k => !requiredLangs.includes(k) && !languages.includes(k));

  // Build the field picker list from props.references['fields'] (TargetField values)
  const availableFields = useMemo(() => {
    const fields = props.references?.['fields'];
    if (!fields || fields.length === 0) return [];
    return dedupeReferenceItems(fields).map(field => ({
      key: referenceValue(field),
      label: referenceLabel(field).replace(/_/g, ' '),
    }));
  }, [props.references]);

  // Set default to 'en' if available, otherwise first language
  const defaultLang = languages.includes('en') ? 'en' : languages[0] || 'en';
  const [selectedLang, setSelectedLang] = useState(defaultLang);

  // HTML is the source of truth for storage. Content previously saved as markdown
  // (detected by NOT starting with '<') is converted to HTML once on load.
  const currentValue = richTextObject.value[selectedLang]?.value || '';
  const currentHtml = loadAsHtml(currentValue);

  // Tracks whether the user has local edits pending in the debounce queue.
  // Prevents the collab sync effect from overwriting in-progress local changes.
  const hasPendingLocalChanges = useRef(false);

  // Debounced update to trackedObject — saves HTML
  const [debouncedUpdate] = useDebounce((lang: string, html: string) => {
    const langTrackedObject = richTextObject.value[lang];
    if (langTrackedObject) {
      langTrackedObject.set(html);
      langTrackedObject.locateUserHere();
      props.onChange?.();
    }
    // Clear AFTER all operations so that any synchronous reactive re-render triggered
    // by .set() still sees hasPendingLocalChanges=true and skips the collab sync effect.
    hasPendingLocalChanges.current = false;
  }, 500);

  const save = useCallback((html: string) => {
    hasPendingLocalChanges.current = true;
    debouncedUpdate(selectedLang, html);
  }, [selectedLang, debouncedUpdate]);

  const { viewMarkdown, htmlContent, setHtmlContent, sourceContent, setSourceContent,
    handleRichTextChange, handleSourceChange, handleToggleMarkdown }
    = useRichTextToggle(currentHtml, save);

  // Update content when language selection changes
  useEffect(() => {
    const newValue = richTextObject.value[selectedLang]?.value || '';
    const newHtml = loadAsHtml(newValue);
    setHtmlContent(newHtml);
    setSourceContent(htmlToMarkdown(newHtml));
  }, [selectedLang]);

  // Sync external changes (from other collaborators)
  useEffect(() => {
    const viewingAuthor = richTextObject?.state?.currentAuthor;
    const langTrackedObject = richTextObject.value[selectedLang];
    const editingAuthorsNotMe = (langTrackedObject?.state?.objectToActivity?.[langTrackedObject?.id] || []).some(
      (ea: UserActivity) => (ea.uid !== viewingAuthor?.uid || ea.tabId !== viewingAuthor?.tabId)
    );

    if (editingAuthorsNotMe && !hasPendingLocalChanges.current) {
      const latestValue = langTrackedObject?.value || '';
      const latestHtml = loadAsHtml(latestValue);
      if (viewMarkdown) {
        // Guard: skip if the incoming HTML is semantically equivalent to the user's current
        // markdown source (round-trip match). Without this, a lossy markdown→HTML round-trip
        // (e.g. "ok\nok\n**" → "<p>ok ok **</p>" → "ok ok \*\*") causes a spurious
        // setSourceContent call that jumps the textarea cursor.
        if (loadAsHtml(sourceContent) !== latestHtml) {
          setSourceContent(htmlToMarkdown(latestHtml));
        }
      }
      if (latestHtml !== htmlContent) {
        setHtmlContent(latestHtml);
      }
    }
  }, [richTextObject.value[selectedLang]?.value, selectedLang, richTextObject.state?.objectToActivity, viewMarkdown, htmlContent, sourceContent]);

  function addLanguage(lang: string) {
    openChangeset(richTextObject.state);
    try {
      const newElement = createFromRawValue(richTextObject.state, '');
      richTextObject.set(lang, newElement);
    } finally {
      closeChangeset(richTextObject.state);
    }
    setSelectedLang(lang);
  }

  function removeLanguage(lang: string) {
    if (requiredLangs.includes(lang)) return;
    const confirmed = window.confirm('Are you sure you want to delete this language?');
    if (confirmed) {
      richTextObject.remove(lang);
      if (selectedLang === lang) {
        setSelectedLang(requiredLangs[0] || languages.find(l => l !== lang) || 'en');
      }
    }
  }

  const showTabs = languages.length > 1 || unusedOptionalLangs.length > 0;

  return (
    <div className="w-full">
      {/* Language tabs with add/remove */}
      {showTabs && (
        <div className="flex items-center border-b border-gray-300 mb-2">
          {languages.map((lang) => (
            <button
              key={lang}
              onClick={() => setSelectedLang(lang)}
              className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
                lang === selectedLang
                  ? 'border-blue-600 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              {nodePropMeta[lang]?.name ?? lang.toUpperCase()}
              {!props.readonly && !requiredLangs.includes(lang) && (
                <span
                  className="ml-1 hover:text-red-600"
                  onClick={(e) => { e.stopPropagation(); removeLanguage(lang); }}
                >
                  ×
                </span>
              )}
            </button>
          ))}
          {!props.readonly && unusedOptionalLangs.length > 0 && (
            <Dropdown
              title="+"
              searchable
              sorted
              options={unusedOptionalLangs.map(lang => ({
                name: nodePropMeta[lang]?.name ?? lang.toUpperCase(),
                callback: () => addLanguage(lang),
              }))}
            />
          )}
        </div>
      )}

      {/* Editor */}
      {viewMarkdown
        ? <MarkdownSourceView sourceContent={sourceContent} onChange={handleSourceChange} onToggle={() => handleToggleMarkdown()} disabled={props.readonly} />
        : <RichTextEditor key={selectedLang} value={htmlContent} onChange={handleRichTextChange} disabled={props.readonly} onToggleMarkdown={handleToggleMarkdown} availableFields={availableFields.length > 0 ? availableFields : undefined} />
      }
    </div>
  );
}
